import type { WorkOrder } from "./types";

const decimalPattern = /^(-?)(\d+)(?:\.(\d+))?$/;

export const decimalUnits = (raw: string, scale: number) => {
  const match = raw.trim().match(decimalPattern);
  if (!match?.[2]) throw new Error(`Valor decimal inválido: ${raw}`);
  const factor = 10n ** BigInt(scale);
  const fraction = (match[3] ?? "").padEnd(scale, "0").slice(0, scale);
  const units = BigInt(match[2]) * factor + BigInt(fraction || "0");
  return match[1] ? -units : units;
};

export const formatDecimalUnits = (units: bigint, scale: number) => {
  const negative = units < 0n;
  const absolute = negative ? -units : units;
  const factor = 10n ** BigInt(scale);
  return `${negative ? "-" : ""}${absolute / factor}.${String(absolute % factor).padStart(scale, "0")}`;
};

export const moneyCents = (raw: string) => decimalUnits(raw, 2);
export const formatMoneyCents = (cents: bigint) => formatDecimalUnits(cents, 2);
export const subtractMoney = (minuend: string, subtrahend: string) =>
  formatMoneyCents(moneyCents(minuend) - moneyCents(subtrahend));

export const workOrderNetAmount = (workOrder: Pick<WorkOrder, "contractedAmount" | "discount" | "surcharge">) =>
  formatMoneyCents(
    moneyCents(workOrder.contractedAmount)
      - moneyCents(workOrder.discount)
      + moneyCents(workOrder.surcharge),
  );

const roundedDivision = (numerator: bigint, denominator: bigint) => {
  if (denominator === 0n) return 0n;
  const negative = (numerator < 0n) !== (denominator < 0n);
  const absoluteNumerator = numerator < 0n ? -numerator : numerator;
  const absoluteDenominator = denominator < 0n ? -denominator : denominator;
  const rounded = (absoluteNumerator + absoluteDenominator / 2n) / absoluteDenominator;
  return negative ? -rounded : rounded;
};

export const percentageTenths = (part: string, total: string) => {
  const denominator = moneyCents(total);
  if (denominator === 0n) return 0n;
  return roundedDivision(moneyCents(part) * 1_000n, denominator);
};

export const boundedPercentage = (part: string, total: string) => {
  const tenths = percentageTenths(part, total);
  const bounded = tenths < 0n ? 0n : tenths > 1_000n ? 1_000n : tenths;
  return Number(bounded) / 10;
};

export const comparisonText = (current: string, previous: string, previousLabel: string) => {
  const previousCents = moneyCents(previous);
  if (previousCents === 0n) return "Sem base anterior";
  const tenths = roundedDivision((moneyCents(current) - previousCents) * 1_000n, previousCents < 0n ? -previousCents : previousCents);
  const absolute = tenths < 0n ? -tenths : tenths;
  return `${tenths >= 0n ? "+" : "-"}${absolute / 10n},${absolute % 10n}% vs. ${previousLabel}`;
};

export const chartHeight = (value: string, maximumCents: bigint) => {
  const cents = moneyCents(value);
  if (cents <= 0n || maximumCents <= 0n) return 2;
  const basisPoints = (cents * 10_000n) / maximumCents;
  const boundedBasisPoints = basisPoints > 10_000n ? 10_000n : basisPoints;
  return Math.max(8, Number(boundedBasisPoints) / 100);
};

export const maximumMoneyCents = (values: string[]) => values.reduce((maximum, value) => {
  const cents = moneyCents(value);
  return cents > maximum ? cents : maximum;
}, 0n);
